<template>
  <div class="tw-p-10 tw-mt-6">
    <div class="tw-flex tw-justify-between">
      <div class="tw-flex-col tw-my-auto">
        <div class="tw-text-3xl tw-text-gray-700">Patient Details</div>
        <div>
          <span class="tw-text-gray-700 tw-text-sm">Report generated on:</span>
          <span class="tw-text-gray-500 tw-text-sm"> {{ dateStampFormatter(new Date().toISOString()) }}</span>
        </div>
        <div>
          <span class="tw-text-gray-700 tw-text-sm">Report generated by:</span>
          <span class="tw-text-gray-500 tw-text-sm"> {{ generatedByUser.name }}</span>
        </div>
        <div v-if="submittedByUser && submittedByUser.name" class="tw-mt-3">
          <div>
            <span class="tw-text-gray-700 tw-text-sm">Rx submitted on:</span>
            <span class="tw-text-gray-500 tw-text-sm">
              {{ dateStampFormatter(rxDetailsDocument && rxDetailsDocument.insertDate ? rxDetailsDocument.insertDate : businessPrescription.submittedDate) }}
            </span>
          </div>
          <div>
            <span class="tw-text-gray-700 tw-text-sm">Rx submitted by:</span>
            <span class="tw-text-gray-500 tw-text-sm"> {{ submittedByUser.name }}</span>
          </div>
        </div>
      </div>
      <img class="logo tw-max-h-20 tw-object-cover" :src="imgSrc" />
    </div>

    <div class="tw-grid tw-grid-cols-3 tw-mt-6 tw-gap-10">
      <div class="tw-flex-col">
        <div class="tw-text-gray-800 tw-text-lg tw-font-medium">Name</div>
        <div class="tw-text-gray-600">{{ user.name }}</div>
        <div class="tw-text-gray-800 tw-text-lg tw-font-medium tw-mt-4">Gender</div>
        <div class="tw-text-gray-600 tw-capitalize">{{ user.gender }}</div>
        <div class="tw-text-gray-800 tw-text-lg tw-font-medium tw-mt-4">Last Appointment</div>
        <div class="tw-text-gray-600 tw-capitalize">{{ lastAppointmentFormatted }}</div>
      </div>
      <div class="tw-flex-col">
        <div class="tw-text-gray-800 tw-text-lg tw-font-medium">DOB</div>
        <div class="tw-text-gray-600">
          {{ dobFormatter(user.dob) }}
        </div>
        <div class="tw-text-gray-800 tw-text-lg tw-font-medium tw-mt-4">Mailing Address</div>
        <div class="tw-text-gray-600">
          <span>{{ user.address.line1 }}</span
          ><span v-show="user.address.line2">, {{ user.address.line2 }}</span>
        </div>
        <div class="tw-text-gray-600">{{ user.address.city }} {{ user.address.state }}, {{ user.address.postalCode }}</div>
      </div>
      <div class="tw-flex-col">
        <div class="tw-text-gray-800 tw-text-lg tw-font-medium">Phone</div>
        <div class="tw-text-gray-600">
          <base-phone-number-field :value="user.phoneNumber" :isHyperlinkActive="false" />
        </div>
        <div class="tw-text-gray-800 tw-text-lg tw-font-medium tw-mt-4">Email Address</div>
        <div class="tw-text-gray-600 tw-text-wrap">{{ user.emailAddress }}</div>
      </div>
    </div>

    <div class="tw-text-3xl tw-text-gray-700 tw-mt-6">Program Details</div>
    <div class="tw-grid tw-grid-cols-3 tw-mt-2">
      <div class="tw-text-gray-800 tw-text-lg tw-font-medium">Program Name</div>
      <div class="tw-text-gray-600 tw-col-span-2">
        {{ businessPrescription.businessProgramName }}
      </div>
    </div>
    <div class="tw-grid tw-grid-cols-3">
      <div class="tw-text-gray-800 tw-text-lg tw-font-medium">Prescription Date</div>
      <div class="tw-text-gray-600 tw-col-span-2">
        {{ customFormatter(businessPrescription.submittedDate) }}
      </div>
    </div>

    <div v-if="submittedByUser" class="tw-mt-2">
      <div class="tw-grid tw-grid-cols-3">
        <div class="tw-text-gray-800 tw-text-lg tw-font-medium">Prescriber</div>
        <div class="tw-text-gray-600 tw-col-span-2">
          {{ sanitizedPrescriberName }}
        </div>
      </div>

      <div class="tw-grid tw-grid-cols-3">
        <div class="tw-text-gray-800 tw-text-lg tw-font-medium">Prescriber E-Signature</div>
        <div class="tw-col-span-2">
          <span class="signature-text">{{ sanitizedPrescriberName }}</span>
        </div>
      </div>
    </div>

    <div v-if="showAlleviateDisclaimer" class="tw-border tw-rounded-md tw-bg-gray-50 tw-mt-3 tw-p-3 tw-flex tw-gap-3">
      <information-icon class="tw-flex-none tw-w-6 tw-h-6 tw-my-auto tw-text-gray-500" />
      <div>
        <div class="tw-text-gray-700 tw-text-sm tw-font-medium">Alleviate Disclaimer</div>
        <div class="tw-text-gray-600 tw-text-xs">
          The above-named Patient (or guardian if Patient is a minor) (PATIENT) by purposes of payment to JOI Group, Inc., is consenting to the collection and testing of the named
          PATIENT by Spiriplex, Inc. (SPIRIPLEX) for sensitivities to specific allergens and to provide a Total lgE value (SERVICE). This consent releases SPIRIPLEX, its affiliates
          and any other entity and individuals associated with this SERVICE from all liability arising from the collection and testing of my blood. Patient acknowledges the results
          for the SERVICE can be affected by many factors and does not constitute medical advice or a diagnosis of any kind. PATIENT authorizes SPIRIPLEX, to release copies of
          PATIENT laboratory results and data to healthcare providers and facilities for the purpose of continuing and coordinating my plan of treatment, quality assurance,
          regulatory or accreditation purposes. PATIENT consents to the release of the results and data of PATIENT SERVICE (without my name being used) in clinical and research
          studies and reports.
        </div>
      </div>
    </div>

    <div class="tw-h-56 tw-mt-6">
      <div class="tw-grid tw-grid-cols-2 tw-gap-3">
        <div class="tw-p-4 tw-rounded-md tw-border tw-border-gray-300 tw-bg-gray-100">
          <div class="tw-text-2xl tw-font-thin" :style="stylePrimaryText">Ordered By</div>
          <div class="tw-text-gray-700">{{ businessLocation.name }}</div>
          <div class="tw-text-gray-700">{{ businessLocation.addressLine1 }}</div>
          <div class="tw-text-gray-700">{{ businessLocation.addressLine2 }}</div>
          <div class="tw-text-gray-700">{{ businessLocation.city }} {{ businessLocation.state }}, {{ businessLocation.postalCode }}</div>
          <div class="tw-text-gray-700">
            <base-phone-number-field :value="businessLocation.phoneNumber" :isHyperlinkActive="false" />
          </div>
        </div>
        <div class="tw-p-4 tw-rounded-md tw-border tw-border-gray-300 tw-bg-gray-100">
          <div class="tw-text-2xl tw-font-thin" :style="stylePrimaryText">Transmitted By</div>
          <div class="tw-text-gray-700">{{ business.name }}</div>
          <div class="tw-text-gray-700">{{ business.address.line1 }}</div>
          <div class="tw-text-gray-700">{{ business.address.line2 }}</div>
          <div class="tw-text-gray-700">{{ business.address.city }} {{ business.address.state }}, {{ business.address.postalCode }}</div>
          <div class="tw-text-gray-700">
            <base-phone-number-field :value="business.phoneNumber" :isHyperlinkActive="false" />
          </div>
        </div>
      </div>
    </div>

    <table class="tw-w-full tw-mt-3 tw-table-auto tw-border-collapse">
      <thead :style="stylePrimaryBackground">
        <tr>
          <th class="tw-py-3 tw-pl-3 tw-rounded-tl-md" scope="col">Prescription Details</th>
          <th class="tw-py-3 tw-pr-3 tw-text-right tw-whitespace-nowrap tw-rounded-tr-md" scope="col">
            Uploaded Date
          </th>
        </tr>
      </thead>
      <tbody>
        <tr class="tw-border-b tw-bg-gray-50" v-for="item in combinedPrescriptionItems" :key="item.uniqueId">
          <td class="tw-p-3 tw-text-left">
            <div>
              <template v-if="item.type === 'medication'">
                <div class="tw-text-gray-700">
                  {{
                    (businessPrescription.programFieldLabel ? businessPrescription.programFieldLabel : "Medication") +
                      " " +
                      (getMedicationIndex(item) + 1) +
                      " : " +
                      cleanMedicationName(item.name)
                  }}
                </div>
                <div class="tw-text-gray-500 tw-text-xs">
                  {{ cleanMedicationName(item.description) }}
                </div>
              </template>
              <template v-else-if="item.type === 'rxItem'">
                <div class="tw-text-gray-700">
                  {{ item.productGroup }}
                  <template v-if="item.groupItemCount > 1">
                    {{ item.groupIndex + 1 }}
                  </template>
                  : {{ item.name }}
                </div>
                <div class="tw-text-gray-500 tw-text-xs">
                  {{ item.description }}
                </div>
              </template>
            </div>
          </td>
          <td class="tw-p-3 tw-text-right tw-whitespace-nowrap">
            <template v-if="item.type === 'medication'">
              {{ customFormatter(item.authorizedAt ? item.authorizedAt : item.insertDate) }}
            </template>
            <template v-else-if="item.type === 'rxItem'">
              {{ customFormatter(item.insertDate) }}
            </template>
          </td>
        </tr>
      </tbody>
    </table>

    <div class="tw-text-lg tw-text-gray-700 tw-mt-8">Rx Notes</div>
    <div class="tw-my-2 tw-grid tw-grid-cols-7" v-for="note in businessPrescription.notes" :key="note.text">
      <div class="tw-text-gray-700">{{ customFormatter(note.insertDate) }}</div>
      <div class="tw-text-gray-700 tw-col-span-6">{{ note.text }}</div>
    </div>

    <div ref="complete" id="complete" v-if="isComplete === true"></div>
  </div>
</template>

<script>
import { mapGetters } from "vuex";
import moment from "moment-timezone";
import isDarkColor from "is-dark-color";
import { InformationIcon } from "@/assets/svgs";
import { STATE_TIMEZONES } from "@/constants/constants";
import { groupBy, orderBy } from "lodash";

export default {
  name: "LifefileOrder",
  props: {
    businessId: String,
    userId: String,
    businessPrescriptionId: String,
    generatedByUserId: String,
    prescriberId: String,
    prescriberName: String,
  },
  components: { InformationIcon },
  data() {
    return {
      business: {
        name: null,
        address: { line1: null, line2: null, city: null, state: null, postalCode: null },
        phoneNumber: null,
        emailAddress: null,
        primaryColor: null,
      },
      businessLocation: {
        name: null,
        addressLine1: null,
        addressLine2: null,
        city: null,
        state: null,
        postalCode: null,
      },
      businessPrescription: {
        businessProgramName: null,
        medications: [],
        rxItems: [],
        notes: [],
        programFieldLabel: null,
      },
      generatedByUser: { name: null },
      submittedByUser: { name: null },
      user: {
        name: null,
        address: { line1: null, line2: null, city: null, state: null, postalCode: null },
        phoneNumber: null,
        emailAddress: null,
        gender: null,
        dob: null,
        lastAppointmentDate: null,
        appointmentLocation: null,
      },
      rxDetailsDocument: null,
      isComplete: false,
      STATE_TIMEZONES,
    };
  },
  computed: {
    ...mapGetters("avatar", ["getVersion"]),
    version() {
      return this.getVersion(this.id);
    },
    imgSrc() {
      return `/api/uploads/businesses/${this.businessId}/avatar/base-image?v=${this.version}`;
    },
    stylePrimaryBackground() {
      if (this.business.primaryColor != null) {
        if (isDarkColor(this.business.primaryColor) === true) {
          return {
            color: "#FFFFFF",
            "background-color": this.business.primaryColor + " !important",
          };
        } else {
          return {
            color: "#66666",
            "background-color": this.business.primaryColor + " !important",
          };
        }
      } else
        return {
          color: "#66666",
          "background-color": "#ff0090 !important",
        };
    },
    showAlleviateDisclaimer() {
      return (
        this.businessPrescription.businessProgramName &&
        (this.businessPrescription.businessProgramName.includes("Alleviate") || this.businessPrescription.businessProgramName.includes("Spiriplex"))
      );
    },
    stylePrimaryText() {
      return {
        color: this.business.primaryColor,
      };
    },
    lastAppointmentFormatted() {
      let result = "";
      if (this.user.lastAppointmentDate) {
        result = this.customFormatter(this.user.lastAppointmentDate);
      }
      if (this.user.appointmentLocation) {
        result += ` (${this.user.appointmentLocation})`;
      }
      return result;
    },
    /**
     * Sanitized prescriber name for display/signature:
     * removes everything after the first comma and trims whitespace.
     */
    sanitizedPrescriberName() {
      if (!this.prescriberName) return "";
      const index = this.prescriberName.indexOf(","); // Extract before comma[web:21][web:29]
      if (index === -1) {
        return this.prescriberName.trim();
      }
      return this.prescriberName.slice(0, index).trim();
    },
    /**
     * Pre-processes Rx items to add groupIndex and groupItemCount for display logic.
     */
    indexedRxItemsByGroup() {
      if (!this.businessPrescription.rxItems || this.businessPrescription.rxItems.length === 0) {
        return [];
      }

      const grouped = groupBy(this.businessPrescription.rxItems, (item) => {
        const groupName = item && item.productGroup;
        return groupName || "Other Rx Items";
      });

      let indexedItems = [];
      Object.keys(grouped).forEach((groupName) => {
        const itemsInGroup = grouped[groupName];
        const sortedItemsInGroup = orderBy(itemsInGroup, [(x) => x.sortOrder, (x) => x.name]);

        sortedItemsInGroup.forEach((item, index) => {
          indexedItems.push({
            ...item,
            type: "rxItem",
            uniqueId: `rxItem-${item.businessRxItemId || item.id || Math.random()}`,
            groupIndex: index,
            groupItemCount: sortedItemsInGroup.length,
            productGroup: groupName,
          });
        });
      });

      indexedItems = orderBy(indexedItems, ["productGroup", "groupIndex"]);

      return indexedItems;
    },
    combinedPrescriptionItems() {
      const combined = [];

      const sortedMedications = orderBy(this.businessPrescription.medications, [(med) => med.sortOrder, (med) => med.name]);
      if (sortedMedications && sortedMedications.length > 0) {
        sortedMedications.forEach((med) => {
          combined.push({
            ...med,
            type: "medication",
            uniqueId: `medication-${med.id || med.businessItemCategoryId || Math.random()}`,
          });
        });
      }
      if (this.indexedRxItemsByGroup && this.indexedRxItemsByGroup.length > 0) {
        combined.push(...this.indexedRxItemsByGroup);
      }
      return combined;
    },
  },
  methods: {
    getLifefileOrder() {
      this.$axios
        .get(
          `/pdf/lifefileorder/businesses/${this.businessId}/users/${this.userId}/prescriptions/${this.businessPrescriptionId}/generatedby/${this.generatedByUserId}/prescriber/${
            this.prescriberId
          }/name/${this.prescriberName}`,
        )
        .then((res) => {
          this.business = res.data.business;
          this.businessLocation = res.data.location;
          this.businessPrescription = res.data.prescription;
          this.generatedByUser = res.data.generatedByUser;
          this.submittedByUser = res.data.submittedByUser;
          this.user = res.data.user;
          this.rxDetailsDocument = res.data.rxDetailsDocument;

          if (!this.businessPrescription.businessProgram) {
            this.$set(this.businessPrescription, "businessProgram", {});
          }
          if (!this.businessPrescription.businessProgram.programFieldLabel) {
            this.$set(this.businessPrescription.businessProgram, "programFieldLabel", "Medication");
          }
        })
        .finally(() => {
          this.complete();
        });
    },
    customFormatter(date) {
      return moment(date).format("MMM DD, YYYY");
    },

    dateStampFormatter(date) {
      let timeZone = "America/New_York";
      if (this.businessLocation && this.businessLocation.state) {
        let state = this.businessLocation.state;
        timeZone = STATE_TIMEZONES[state] || "America/New_York";
      }
      return moment
        .utc(date)
        .tz(timeZone)
        .format("MMM DD, YYYY, hh:mm A (z)");
    },
    dobFormatter(date) {
      return moment(date).format("MM/DD/YYYY");
    },
    complete() {
      this.isComplete = true;
    },
    cleanMedicationName(name) {
      if (typeof name !== "string" || name === null || name === undefined) {
        return name;
      }
      const regex = /(sig:\s*)(.*?)(:\s*)(.*)/i;

      if (regex.test(name)) {
        return name.replace(regex, "$1 $4");
      }
      return name;
    },
    getMedicationIndex(item) {
      const sortedOriginalMedications = orderBy(this.businessPrescription.medications, [(med) => med.sortOrder, (med) => med.name]);

      return sortedOriginalMedications.findIndex(
        (med) =>
          (med.id && med.id === item.id) ||
          (med.businessItemCategoryId && med.businessItemCategoryId === item.businessItemCategoryId) ||
          (med.name === item.name && med.insertDate === item.insertDate),
      );
    },
  },
  mounted() {
    this.getLifefileOrder();
  },
};
</script>

<style scoped>
@import url("https://fonts.googleapis.com/css2?family=Allura&family=Alex+Brush&display=swap");

body {
  margin: 0;
  padding: 0;
  height: 100%;
}

.signature-text {
  font-family: "Allura", "Alex Brush", "Dancing Script", cursive;
  font-size: 24px;
  font-weight: 400;
  line-height: 1.2;
  font-style: normal;
  letter-spacing: 0.05em;
}
</style>
